import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invoicesAPI } from '../services/api';

export const useInvoices = () => {
  const queryClient = useQueryClient();

  // Get all invoices
  const {
    data: rawInvoices = [],
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['invoices'],
    queryFn: invoicesAPI.getAll,
  });

  // Sort invoices: first by issueDate (desc), then by invoice number (desc) for same dates
  const invoices = [...rawInvoices].sort((a: any, b: any) => {
    // First sort by issue date (most recent first)
    const dateA = new Date(a.issueDate || 0);
    const dateB = new Date(b.issueDate || 0);
    const dateComparison = dateB.getTime() - dateA.getTime();
    
    // If dates are different, use date sorting
    if (dateComparison !== 0) {
      return dateComparison;
    }
    
    // If dates are the same, sort by invoice number (highest first)
    const getInvoiceNumber = (invoiceNumber: string) => {
      const match = invoiceNumber.match(/^(\d+)/);
      return match ? parseInt(match[1], 10) : 0;
    };
    
    const numA = getInvoiceNumber(a.invoiceNumber);
    const numB = getInvoiceNumber(b.invoiceNumber);
    return numB - numA;
  });

  // Create invoice mutation
  const createInvoice = useMutation({
    mutationFn: invoicesAPI.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  // Update invoice mutation
  const updateInvoice = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => 
      invoicesAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  // Delete invoice mutation
  const deleteInvoice = useMutation({
    mutationFn: invoicesAPI.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  // Generate PDF mutation
  const generatePDF = useMutation({
    mutationFn: invoicesAPI.generatePDF,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
  });

  return {
    invoices,
    isLoading,
    error,
    refetch,
    createInvoice: createInvoice.mutate,
    updateInvoice: updateInvoice.mutate,
    deleteInvoice: deleteInvoice.mutate,
    generatePDF: generatePDF.mutate,
    isCreating: createInvoice.isPending,
    isUpdating: updateInvoice.isPending,
    isDeleting: deleteInvoice.isPending,
    isGeneratingPDF: generatePDF.isPending,
  };
};

export const useInvoice = (id: string) => {
  const {
    data: invoice,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => invoicesAPI.getById(id),
    enabled: !!id,
  });

  return {
    invoice,
    isLoading,
    error,
    refetch,
  };
};
